import clsx from 'clsx'
import {ReactNode, useId} from 'react'
import './CatalogChoice.css'

type CatalogChoiceOption<T> = {
  value: T
  label: ReactNode
  /** A Line Awesome icon, e.g. 'la-dolly'. */
  icon?: string
  description?: ReactNode
  disabled?: boolean
}

type CatalogChoiceProps<T> = {
  legend: ReactNode
  options: CatalogChoiceOption<T>[]
  value: T | undefined
  onChange: (value: T) => void
  isRequired?: boolean
  isDisabled?: boolean
  hint?: ReactNode
  /**
   * 'row': equal segments on one line (two or three short options).
   * 'grid': two a row on a phone, all on one line when there is room (four options).
   * 'stack': one a row on a phone (long labels).
   */
  layout?: 'row' | 'grid' | 'stack'
  className?: string
}

/**
 * One choice among a few options as a single control (507): each option is its whole label, a 44px target, so a radio
 * no longer sits apart from its words. Real radio inputs keep the arrow keys and a screen reader's «۱ از ۲».
 */
function CatalogChoice<T extends string | number>({
  legend,
  options,
  value,
  onChange,
  isRequired = false,
  isDisabled = false,
  hint,
  layout = 'row',
  className,
}: CatalogChoiceProps<T>) {
  const name = useId()
  const hintId = `${name}-hint`

  return (
    <fieldset
      className={clsx('mp-catalog-choice', `is-${layout}`, className)}
      disabled={isDisabled}
      aria-describedby={hint ? hintId : undefined}
    >
      <legend className='mp-catalog-choice__legend fw-bold fs-6'>
        {legend}
        {isRequired && <span className='required' title='ضروری'></span>}
      </legend>
      <div className='mp-catalog-choice__options'>
        {options.map((option, index) => {
          const id = `${name}-${index}`
          const checked = value === option.value

          return (
            <label
              key={String(option.value)}
              htmlFor={id}
              className={clsx('mp-catalog-choice__option', {
                'is-checked': checked,
                'is-disabled': option.disabled,
              })}
            >
              <input
                id={id}
                type='radio'
                className='mp-catalog-choice__input'
                name={name}
                value={String(option.value)}
                checked={checked}
                disabled={option.disabled}
                required={isRequired}
                onChange={() => onChange(option.value)}
              />
              {option.icon && (
                <i className={clsx('las mp-catalog-choice__icon', option.icon)} aria-hidden='true'></i>
              )}
              <span className='mp-catalog-choice__text'>
                <span className='mp-catalog-choice__label'>{option.label}</span>
                {option.description && (
                  <span className='mp-catalog-choice__description'>{option.description}</span>
                )}
              </span>
            </label>
          )
        })}
      </div>
      {hint && (
        <div id={hintId} className='mp-catalog-choice__hint'>
          {hint}
        </div>
      )}
    </fieldset>
  )
}

export {CatalogChoice}
export type {CatalogChoiceOption}
